/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


customElements.whenDefined('product-variants').then(function () {
  var Klass = customElements.get('product-variants');
  if (!Klass || !Klass.prototype._updateDisableSelectors) return;

  Klass.prototype._updateDisableSelectors = function () {
    var selectedVariant = this.selectedVariant;
    if (!selectedVariant) return;

    var hideSoldOutVariants = this.hideSoldOutVariants;
    var optionSelectors = this.optionSelectors;
    var product = this.product;

    // Size buttons are re-ordered in Liquid (size-values-sorted), so never rely on
    // product.options[].values index / nth-child. Always resolve the swatch by value.
    var getSelectorElement = function (selector, selectorType, value) {
      if (selectorType === 'dropdown') {
        return selector.querySelector('.combo-box__option-item[value="' + CSS.escape(value) + '"]');
      }

      var input = selector.querySelector('input[type="radio"][value="' + CSS.escape(value) + '"]');
      return input && input.closest('.color-swatch, .variant-swatch, .block-swatch');
    };

    var applyClassToSelector = function (selector, available, hasAtLeastOneCombination, value) {
      var selectorType = selector.getAttribute('data-selector-type');
      var element = getSelectorElement(selector, selectorType, value);
      if (!element) return;

      // 没有任何变体组合的值才隐藏；有变体但售罄的值保留展示并置灰
      element.toggleAttribute('hidden', !hasAtLeastOneCombination);

      if (hideSoldOutVariants) {
        element.toggleAttribute('hidden', !available);
      } else {
        element.classList.toggle('is-disabled', !available);
      }
    };

    if (optionSelectors && optionSelectors[0]) {
      product.options[0].values.forEach(function (value) {
        var hasAtLeastOneCombination = product.variants.some(function (variant) {
          return variant.option1 === value && variant;
        });
        var hasAvailableVariant = product.variants.some(function (variant) {
          return variant.option1 === value && variant.available;
        });

        applyClassToSelector(optionSelectors[0], hasAvailableVariant, hasAtLeastOneCombination, value);

        if (optionSelectors[1]) {
          product.options[1].values.forEach(function (value2) {
            var hasAtLeastOneCombination2 = product.variants.some(function (variant) {
              return variant.option2 === value2 && variant.option1 === selectedVariant.option1 && variant;
            });
            var hasAvailableVariant2 = product.variants.some(function (variant) {
              return variant.option2 === value2 && variant.option1 === selectedVariant.option1 && variant.available;
            });

            applyClassToSelector(optionSelectors[1], hasAvailableVariant2, hasAtLeastOneCombination2, value2);

            if (optionSelectors[2]) {
              product.options[2].values.forEach(function (value3) {
                var hasAtLeastOneCombination3 = product.variants.some(function (variant) {
                  return variant.option3 === value3 &&
                    variant.option1 === selectedVariant.option1 &&
                    variant.option2 === selectedVariant.option2 &&
                    variant;
                });
                var hasAvailableVariant3 = product.variants.some(function (variant) {
                  return variant.option3 === value3 &&
                    variant.option1 === selectedVariant.option1 &&
                    variant.option2 === selectedVariant.option2 &&
                    variant.available;
                });

                applyClassToSelector(optionSelectors[2], hasAvailableVariant3, hasAtLeastOneCombination3, value3);
              });
            }
          });
        }
      });
    }

    var notifyDiv = document.getElementById('notifyDiv');
    if (notifyDiv) {
      if (selectedVariant.available) {
        notifyDiv.innerHTML = '';
      } else {
        notifyDiv.innerHTML = '<a class="klaviyo-button klaviyo-bis-trigger " href="#">Notify Me When Available</a>';
      }
    }
  };

  // If product-variants already initialized with the original nth-child filter
  // (before this patch), re-run so combination sizes hide on first paint too.
  var reapplyDisableSelectors = function () {
    document.querySelectorAll('product-variants').forEach(function (el) {
      if (el.product && typeof el._updateDisableSelectors === 'function') {
        el._updateDisableSelectors();
      }
    });
  };

  reapplyDisableSelectors();
  // ProductLoader is async; catch instances that finish loading right after the patch.
  setTimeout(reapplyDisableSelectors, 0);
  setTimeout(reapplyDisableSelectors, 300);
});

/**
 * 颜色分组标题：在对应分组标题后显示当前选中的颜色名（与 sx 项目一致）
 */
function initColorGroupTitles(root) {
  var scope = root || document;
  scope.querySelectorAll('.product-form__option-selector[data-selector-type="color"]').forEach(function (optionSelector) {
    if (optionSelector.dataset.colorGroupInitialized === 'true') return;

    var groupedTitle = optionSelector.querySelector('.color-group-selected-value[data-color-group="grouped"]');
    var ungroupedTitle = optionSelector.querySelector('.color-group-selected-value[data-color-group="ungrouped"]');
    if (!groupedTitle && !ungroupedTitle) return;

    optionSelector.dataset.colorGroupInitialized = 'true';

    var colorInputs = optionSelector.querySelectorAll('input.color-swatch__radio[type="radio"]');
    if (!colorInputs.length) return;

    function updateColorGroupTitles() {
      if (groupedTitle) groupedTitle.textContent = '';
      if (ungroupedTitle) ungroupedTitle.textContent = '';

      var selectedInput = optionSelector.querySelector('input.color-swatch__radio[type="radio"]:checked');
      if (!selectedInput) return;

      var selectedValue = selectedInput.value;
      var colorGroup = selectedInput.getAttribute('data-color-group');

      if (colorGroup === 'grouped' && groupedTitle) {
        groupedTitle.textContent = selectedValue;
      } else if (colorGroup === 'ungrouped' && ungroupedTitle) {
        ungroupedTitle.textContent = selectedValue;
      }
    }

    colorInputs.forEach(function (input) {
      input.addEventListener('change', updateColorGroupTitles);
    });

    var formId = optionSelector.closest('product-variants') && optionSelector.closest('product-variants').getAttribute('form-id');
    var productForm = formId && document.getElementById(formId);
    if (productForm) {
      productForm.addEventListener('variant:changed', updateColorGroupTitles);
    }

    updateColorGroupTitles();
  });
}

document.addEventListener('DOMContentLoaded', function () {
  initColorGroupTitles();
});

document.addEventListener('shopify:section:load', function (event) {
  initColorGroupTitles(event.target);
});

document.addEventListener('change', function (event) {
  var sortInput = event.target.closest('#facet-filters-form input[type="radio"][name="sort_by"]');
  if (!sortInput) return;

  var facetFilters = sortInput.closest('facet-filters');
  var form = sortInput.closest('form');
  if (!facetFilters || !form) return;

  var searchParams = new URLSearchParams(new FormData(form));
  facetFilters.dispatchEvent(new CustomEvent('facet:criteria-changed', {
    bubbles: true,
    detail: {
      url: window.location.pathname + '?' + searchParams.toString()
    }
  }));
});

// Featured / Sort by must open the popover, never the facet drawer.
document.addEventListener('click', function (event) {
  var sortButton = event.target.closest('.product-facet__meta-bar-item--sort .popover-button, .mobile-toolbar__item--sort');
  if (!sortButton) return;

  var sortPopover = document.getElementById('sort-by-popover');
  if (!sortPopover) return;

  sortButton.setAttribute('aria-controls', 'sort-by-popover');

  var facetFilters = document.getElementById('facet-filters');
  if (facetFilters && facetFilters.hasAttribute('open')) {
    facetFilters.open = false;
  }

  document.querySelectorAll('.product-facet__meta-bar-item--filter[aria-controls="facet-filters"][aria-expanded="true"]').forEach(function (btn) {
    btn.setAttribute('aria-expanded', 'false');
  });
}, true);

// Collection swatches: show +N after 5 colors; mobile tap expands, desktop uses CSS :hover.
(function () {
  var fineHoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  function collapseSwatchLists(exceptList) {
    document.querySelectorAll('[data-swatch-collapsible].is-expanded').forEach(function (list) {
      if (exceptList && list === exceptList) return;
      list.classList.remove('is-expanded');
      var moreButton = list.querySelector('[data-swatch-more]');
      if (moreButton) moreButton.setAttribute('aria-expanded', 'false');
    });
  }

  document.addEventListener('click', function (event) {
    if (fineHoverQuery.matches) return;

    var list = event.target.closest('[data-swatch-collapsible]');
    if (!list) {
      collapseSwatchLists();
      return;
    }

    if (list.classList.contains('is-expanded')) return;

    event.preventDefault();
    event.stopPropagation();
    collapseSwatchLists(list);
    list.classList.add('is-expanded');
    var moreButton = list.querySelector('[data-swatch-more]');
    if (moreButton) moreButton.setAttribute('aria-expanded', 'true');
  }, true);
})();


/*
 * Product detail (PC) — Plan 1: compact switchable slot to the visible image width.
 * - Size from CSS tokens (slot-max × aspect-ratio), not "expand to full then shrink"
 * - Avoids centered-row jump on first paint / color change
 * - No mid-zoom writes (zoom lock); sync on load / slide / settle only
 * - Scoped: body.page--product + thumbnails-position=left
 */
(function () {
  var pocketQuery = window.matchMedia('(max-width: 999px)');
  var lastSync = new WeakMap();
  var zoomLock = false;
  var zoomUnlockTimer = null;
  var settleTimer = null;
  var layoutReady = false;

  function markLayoutReady() {
    if (!document.body) return;
    layoutReady = true;
    document.body.classList.add('pdp-plan1-ready');
  }

  function getSelectedImageWrapper(media) {
    var item =
      media.querySelector('.product__media-item.is-selected:not(.is-filtered)') ||
      media.querySelector('.product__media-item.is-initial-selected:not(.is-filtered)') ||
      media.querySelector('.product__media-item:not(.is-filtered)') ||
      media.querySelector('.product__media-item.is-selected') ||
      media.querySelector('.product__media-item');
    if (!item) return null;
    return (
      item.querySelector('.product__media-image-wrapper') ||
      item.querySelector('.video-wrapper') ||
      item.querySelector('.model-wrapper') ||
      item.firstElementChild ||
      item
    );
  }

  function getOuterListWrapper(media) {
    for (var i = 0; i < media.children.length; i++) {
      var child = media.children[i];
      if (child.classList && child.classList.contains('product__media-list-wrapper')) {
        return child;
      }
    }
    return null;
  }

  function refreshProductFlickity(media) {
    var carousel = media.querySelector('flickity-carousel');
    if (!carousel || !carousel.flickityInstance) return;
    carousel.flickityInstance.then(function (instance) {
      if (instance && typeof instance.resize === 'function') {
        instance.resize();
      }
    });
  }

  function clearCompactLayout(media) {
    var nav = media.querySelector('.product__media-nav');
    var outer = getOuterListWrapper(media);
    media.style.removeProperty('--pdp-main-slot-width');
    media.style.removeProperty('width');
    if (outer) {
      outer.style.removeProperty('width');
      outer.style.removeProperty('max-width');
    }
    if (nav) {
      nav.style.removeProperty('height');
      nav.style.removeProperty('max-height');
    }
    if (document.body) {
      document.body.style.removeProperty('--pdp-col-media-live');
    }
    lastSync.delete(media);
  }

  /**
   * Prefer token math (independent of current live slot width) so we never need
   * to expand to full budget before measuring a new slide.
   */
  function measureTargetSize(media, image, nav) {
    var mediaStyles = getComputedStyle(media);
    var imageStyles = getComputedStyle(image);
    var slotMax = parseFloat(mediaStyles.getPropertyValue('--pdp-main-slot-width-max'));
    var maxH = parseFloat(mediaStyles.getPropertyValue('--pdp-main-image-max-height'));
    var thumbColW = parseFloat(mediaStyles.getPropertyValue('--thumb-column'));
    var ar = parseFloat(imageStyles.getPropertyValue('--aspect-ratio'));

    if (!(maxH > 0) && document.body) {
      maxH = parseFloat(getComputedStyle(document.body).getPropertyValue('--pdp-main-image-max-height'));
    }

    if (!(thumbColW > 40) && nav) {
      thumbColW = Math.round(nav.getBoundingClientRect().width);
    }

    if (ar > 0 && slotMax > 40 && maxH > 40) {
      var displayWidth = Math.round(Math.min(slotMax, maxH * ar));
      var displayHeight = Math.round(displayWidth / ar);
      if (displayWidth > 40 && displayHeight > 40 && thumbColW > 40) {
        return {
          displayWidth: displayWidth,
          displayHeight: displayHeight,
          thumbColW: Math.round(thumbColW)
        };
      }
    }

    var imageRect = image.getBoundingClientRect();
    var displayWidthFallback = Math.round(imageRect.width);
    var displayHeightFallback = Math.round(imageRect.height);
    var thumbFallback = thumbColW > 40 ? Math.round(thumbColW) : Math.round(nav.getBoundingClientRect().width);

    if (!(displayWidthFallback > 40) || !(displayHeightFallback > 40) || !(thumbFallback > 40)) {
      return null;
    }

    return {
      displayWidth: displayWidthFallback,
      displayHeight: displayHeightFallback,
      thumbColW: thumbFallback
    };
  }

  function syncCompactToImage(media) {
    if (!document.body || !document.body.classList.contains('page--product')) return false;
    if (!media || media.getAttribute('thumbnails-position') !== 'left') return false;
    if (pocketQuery.matches) {
      clearCompactLayout(media);
      markLayoutReady();
      return true;
    }
    if (zoomLock) return false;

    var image = getSelectedImageWrapper(media);
    var nav = media.querySelector('.product__media-nav');
    var outer = getOuterListWrapper(media);
    if (!image || !nav || !outer) return false;

    var measured = measureTargetSize(media, image, nav);
    if (!measured) return false;

    var displayWidth = measured.displayWidth;
    var displayHeight = measured.displayHeight;
    var mediaWidth = Math.round(measured.thumbColW + displayWidth);
    var prev = lastSync.get(media);
    var unchanged =
      prev &&
      prev.displayWidth === displayWidth &&
      prev.displayHeight === displayHeight &&
      prev.mediaWidth === mediaWidth;

    if (!unchanged) {
      lastSync.set(media, {
        displayWidth: displayWidth,
        displayHeight: displayHeight,
        mediaWidth: mediaWidth
      });

      media.style.setProperty('--pdp-main-slot-width', displayWidth + 'px');
      media.style.width = mediaWidth + 'px';
      outer.style.width = displayWidth + 'px';
      outer.style.maxWidth = displayWidth + 'px';
      nav.style.height = displayHeight + 'px';
      nav.style.maxHeight = displayHeight + 'px';
      document.body.style.setProperty('--pdp-col-media-live', mediaWidth + 'px');
      refreshProductFlickity(media);
    }

    markLayoutReady();
    return true;
  }

  function syncAll() {
    if (!document.body || !document.body.classList.contains('page--product')) return;
    if (zoomLock) return;
    var synced = false;
    document.querySelectorAll('product-media.product__media').forEach(function (media) {
      if (syncCompactToImage(media)) synced = true;
    });
    if (!synced && !pocketQuery.matches) return;
  }

  function scheduleSync() {
    if (zoomLock) return;
    window.requestAnimationFrame(function () {
      if (zoomLock) return;
      syncAll();
    });
  }

  function scheduleSyncAfterSettle() {
    if (settleTimer) window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(function () {
      settleTimer = null;
      scheduleSync();
    }, 280);
  }

  function beginZoomGesture() {
    if (!document.body || !document.body.classList.contains('page--product')) return;
    if (pocketQuery.matches) return;
    zoomLock = true;
    if (zoomUnlockTimer) window.clearTimeout(zoomUnlockTimer);
    zoomUnlockTimer = window.setTimeout(function () {
      zoomUnlockTimer = null;
      zoomLock = false;
      scheduleSync();
    }, 360);
  }

  function whenSelectedImageReady(media, callback) {
    var image = getSelectedImageWrapper(media);
    if (!image) {
      callback();
      return;
    }
    var img = image.tagName === 'IMG' ? image : image.querySelector('img');
    if (!img || (img.complete && img.naturalWidth > 0)) {
      callback();
      return;
    }
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      callback();
    }
    img.addEventListener('load', finish, { once: true });
    img.addEventListener('error', finish, { once: true });
    window.setTimeout(finish, 400);
  }

  function observeMedia(media) {
    if (media.dataset.pdpPlan1CompactSynced === 'true') return;
    media.dataset.pdpPlan1CompactSynced = 'true';

    var carousel = media.querySelector('flickity-carousel');
    if (!carousel) return;

    function onSlide() {
      // Keep current live widths; remeasure target from tokens / new slide (no full-width flash).
      lastSync.delete(media);
      whenSelectedImageReady(media, function () {
        window.requestAnimationFrame(function () {
          syncCompactToImage(media);
        });
      });
    }

    carousel.addEventListener('flickity:slide-changed', onSlide);
    carousel.addEventListener('flickity:slide-settled', onSlide);
    carousel.addEventListener('flickity:ready', function () {
      whenSelectedImageReady(media, scheduleSync);
    });
  }

  function init() {
    if (!document.body || !document.body.classList.contains('page--product')) return;
    document.querySelectorAll('product-media.product__media').forEach(function (media) {
      observeMedia(media);
      whenSelectedImageReady(media, function () {
        syncCompactToImage(media);
      });
    });
    scheduleSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Safety: never leave the gallery hidden if measure fails.
  window.setTimeout(markLayoutReady, 900);

  window.addEventListener('resize', function () {
    if (zoomLock) return;
    scheduleSyncAfterSettle();
  });

  window.addEventListener(
    'wheel',
    function (event) {
      if (event.ctrlKey || event.metaKey) beginZoomGesture();
    },
    { passive: true }
  );

  window.addEventListener('keydown', function (event) {
    if (!event.ctrlKey && !event.metaKey) return;
    var key = event.key;
    if (key === '+' || key === '-' || key === '=' || key === '_' || key === '0') {
      beginZoomGesture();
    }
  });

  document.addEventListener(
    'variant:changed',
    function () {
      document.querySelectorAll('product-media.product__media').forEach(function (media) {
        lastSync.delete(media);
        whenSelectedImageReady(media, function () {
          scheduleSync();
          window.setTimeout(function () {
            syncCompactToImage(media);
          }, 50);
        });
      });
    },
    true
  );

  if (typeof pocketQuery.addEventListener === 'function') {
    pocketQuery.addEventListener('change', init);
  } else if (typeof pocketQuery.addListener === 'function') {
    pocketQuery.addListener(init);
  }

  window.setTimeout(init, 300);
  window.setTimeout(scheduleSync, 1000);
})();

/**
 * Phase 4 Theme Bridge — visitor attribution collect + gated bind client.
 * - Cookie `_cse_vid`: UUID, 30d, Secure + SameSite=Lax
 * - POST /attribution/collect (whitelist fields; never email/phone/name/customer_id)
 * - session_id: GA4 native from `_ga_<MEASUREMENT>` (GS1/GS2 digits only); omit if missing — never invent sess_xxx
 * - client_id: from `_ga` only
 * - Bind: GET /apps/cse-bind-token → POST /attribution/bind (visitor_id + bind_token only)
 * - bindEnabled default false: must not call bind-token or /attribution/bind
 * - Does not call gtag / dataLayer sign_up / GA4
 */
(function () {
  var COOKIE_NAME = '_cse_vid';
  var COOKIE_MAX_AGE = 30 * 24 * 60 * 60;
  var BIND_DONE_PREFIX = '_cse_bind_done:';
  var FORBIDDEN_KEYS = {
    email: true,
    phone: true,
    name: true,
    customer_id: true,
    customerId: true,
    first_name: true,
    last_name: true
  };

  var defaults = {
    enabled: true,
    /** Full URL to Worker collect endpoint; leave empty to skip network. */
    collectUrl: '',
    shopDomain: 'hexinlingerie.myshopify.com',
    /**
     * GA4 Measurement ID for session cookie `_ga_<SUFFIX>`.
     * Site stream: G-3R6VKSCYTB (via GTM-KDQT83S5 / Shopify google_tag_ids).
     */
    gaMeasurementId: 'G-3R6VKSCYTB',
    /** Keep false until Theme bind is explicitly enabled (Worker flag is separate). */
    bindEnabled: false,
    bindUrl: '',
    bindTokenUrl: ''
  };

  var config = {};
  var key;
  for (key in defaults) {
    if (Object.prototype.hasOwnProperty.call(defaults, key)) {
      config[key] = defaults[key];
    }
  }
  var incoming = window.__wdAttributionBridge || {};
  for (key in incoming) {
    if (Object.prototype.hasOwnProperty.call(incoming, key)) {
      config[key] = incoming[key];
    }
  }
  window.__wdAttributionBridge = config;

  var bindInFlight = false;

  function uuidV4() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function isUuid(value) {
    return (
      typeof value === 'string' &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        value
      )
    );
  }

  function readCookie(name) {
    var parts = String(document.cookie || '').split(';');
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].trim();
      if (part.indexOf(name + '=') === 0) {
        return decodeURIComponent(part.slice(name.length + 1));
      }
    }
    return null;
  }

  function writeVidCookie(visitorId) {
    var secure =
      typeof location !== 'undefined' && location.protocol === 'https:'
        ? '; Secure'
        : '';
    document.cookie =
      COOKIE_NAME +
      '=' +
      encodeURIComponent(visitorId) +
      '; Path=/' +
      '; Max-Age=' +
      COOKIE_MAX_AGE +
      '; SameSite=Lax' +
      secure;
  }

  function ensureVisitorId() {
    var existing = readCookie(COOKIE_NAME);
    if (isUuid(existing)) {
      writeVidCookie(existing);
      return existing;
    }
    var created = uuidV4();
    writeVidCookie(created);
    return created;
  }

  function isValidGaSessionId(value) {
    if (value == null) return false;
    var s = String(value).trim();
    if (!/^\d+$/.test(s)) return false;
    if (s === '0') return false;
    if (s.length < 6 || s.length > 20) return false;
    return true;
  }

  function gaMeasurementIdToSessionCookieName(measurementId) {
    var id = String(measurementId || '')
      .trim()
      .toUpperCase();
    var match = /^G-([A-Z0-9]+)$/.exec(id);
    if (!match) return null;
    return '_ga_' + match[1];
  }

  /**
   * GA4 native session_id from `_ga_<MEASUREMENT>` cookie (GS1 / GS2).
   * Must be digits only — never invent sess_xxx / UUID.
   */
  function parseGaSessionIdFromCookie(cookieValue) {
    if (cookieValue == null) return null;
    var raw = String(cookieValue).trim();
    if (!raw) return null;
    try {
      raw = decodeURIComponent(raw);
    } catch (error) {}

    if (/^\d+\.\d+$/.test(raw) || /^GA\d+\./i.test(raw)) {
      return null;
    }

    var gs2 = /(?:^|[;.])GS\d+\.\d+\.s(\d+)(?:\$|$)/i.exec(';' + raw);
    if (gs2 && isValidGaSessionId(gs2[1])) return gs2[1];

    var gs2Loose = /\.s(\d+)\$/i.exec(raw);
    if (gs2Loose && isValidGaSessionId(gs2Loose[1])) return gs2Loose[1];

    var gs1 = /(?:^|[;.])GS\d+\.\d+\.(\d+)\./i.exec(';' + raw);
    if (gs1 && isValidGaSessionId(gs1[1])) return gs1[1];

    return null;
  }

  function listGaSessionCookieNames() {
    var names = [];
    var preferred = gaMeasurementIdToSessionCookieName(config.gaMeasurementId);
    if (preferred) names.push(preferred);

    var parts = String(document.cookie || '').split(';');
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].trim();
      if (!part) continue;
      var eq = part.indexOf('=');
      var name = eq >= 0 ? part.slice(0, eq) : part;
      if (name === '_ga') continue;
      if (name.indexOf('_ga_') !== 0) continue;
      if (preferred && name === preferred) continue;
      names.push(name);
    }
    return names;
  }

  /** Read GA4 native session_id; null if cookie missing (do not synthesize). */
  function readGaSessionId() {
    var names = listGaSessionCookieNames();
    for (var i = 0; i < names.length; i++) {
      var value = readCookie(names[i]);
      var sessionId = parseGaSessionIdFromCookie(value);
      if (sessionId) return sessionId;
    }
    return null;
  }

  function bindDoneKey(visitorId) {
    return BIND_DONE_PREFIX + visitorId;
  }

  function hasBoundThisSession(visitorId) {
    try {
      return sessionStorage.getItem(bindDoneKey(visitorId)) === '1';
    } catch (error) {
      return false;
    }
  }

  function markBoundThisSession(visitorId) {
    try {
      sessionStorage.setItem(bindDoneKey(visitorId), '1');
    } catch (error) {}
  }

  function queryParam(name) {
    try {
      return new URLSearchParams(window.location.search).get(name);
    } catch (error) {
      return null;
    }
  }

  function readGaClientId() {
    var ga = readCookie('_ga');
    if (!ga) return null;
    var parts = ga.split('.');
    if (parts.length >= 4) {
      var clientId = parts[parts.length - 2] + '.' + parts[parts.length - 1];
      if (/^\d+\.\d+$/.test(clientId)) return clientId;
    }
    return null;
  }

  function analyticsAllowed() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') {
        return !!privacy.analyticsProcessingAllowed();
      }
    } catch (error) {}
    return true;
  }

  function stripForbiddenKeys(payload) {
    for (var forbidden in FORBIDDEN_KEYS) {
      if (Object.prototype.hasOwnProperty.call(payload, forbidden)) {
        delete payload[forbidden];
      }
    }
    return payload;
  }

  function buildCollectPayload(visitorId) {
    var payload = {
      visitor_id: visitorId,
      shop_domain: config.shopDomain,
      captured_at: new Date().toISOString()
    };

    var sessionId = readGaSessionId();
    if (sessionId) payload.session_id = sessionId;

    var clientId = readGaClientId();
    if (clientId) payload.client_id = clientId;

    var keys = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'gclid',
      'wbraid',
      'gbraid'
    ];
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      var value = queryParam(key);
      if (value) payload[key] = value;
    }

    return stripForbiddenKeys(payload);
  }

  function buildBindPayload(visitorId, bindToken) {
    return stripForbiddenKeys({
      visitor_id: visitorId,
      bind_token: bindToken
    });
  }

  function assertNoPii(payload) {
    for (var key in FORBIDDEN_KEYS) {
      if (
        Object.prototype.hasOwnProperty.call(payload, key) &&
        payload[key] != null &&
        String(payload[key]).length
      ) {
        return false;
      }
    }
    return true;
  }

  function isBindPayloadSafe(payload) {
    if (!payload || !payload.visitor_id || !payload.bind_token) return false;
    if (!assertNoPii(payload)) return false;
    if (Object.prototype.hasOwnProperty.call(payload, 'customer_id')) return false;
    if (Object.prototype.hasOwnProperty.call(payload, 'customerId')) return false;
    return true;
  }

  function postCollect(payload) {
    if (!config.collectUrl) return;
    if (!assertNoPii(payload)) return;

    try {
      fetch(config.collectUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'omit',
        keepalive: true
      }).catch(function () {});
    } catch (error) {}
  }

  function fetchBindToken() {
    return fetch(config.bindTokenUrl, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('bind_token_http_' + response.status);
      }
      return response.json();
    }).then(function (data) {
      var token = data && data.bind_token != null ? String(data.bind_token).trim() : '';
      if (!token) {
        throw new Error('bind_token_missing');
      }
      return token;
    });
  }

  function postBind(payload) {
    if (!isBindPayloadSafe(payload)) {
      return Promise.reject(new Error('bind_payload_unsafe'));
    }

    return fetch(config.bindUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'omit',
      keepalive: true
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('bind_http_' + response.status);
      }
      return response;
    });
  }

  /**
   * When bindEnabled is false: absolute no-op (no bind-token, no /attribution/bind).
   * When true: GET App Proxy bind-token, then POST visitor_id + bind_token only.
   * Failures are swallowed and must not affect registration / page UX.
   */
  function maybeBind() {
    if (!config.bindEnabled) return;
    if (!config.bindUrl || !config.bindTokenUrl) return;
    if (bindInFlight) return;

    var visitorId = ensureVisitorId();
    if (!isUuid(visitorId)) return;
    if (hasBoundThisSession(visitorId)) return;

    bindInFlight = true;
    fetchBindToken()
      .then(function (bindToken) {
        return postBind(buildBindPayload(visitorId, bindToken));
      })
      .then(function () {
        markBoundThisSession(visitorId);
      })
      .catch(function () {})
      .then(function () {
        bindInFlight = false;
      });
  }

  function runCollect() {
    if (!config.enabled) return;

    var visitorId = ensureVisitorId();
    if (!analyticsAllowed()) return;

    var payload = buildCollectPayload(visitorId);
    if (!assertNoPii(payload)) return;
    postCollect(payload);
  }

  function initBridge() {
    runCollect();
    maybeBind();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBridge);
  } else {
    initBridge();
  }

  window.__wdAttributionBridgeEnsureVisitorId = ensureVisitorId;
  window.__wdAttributionBridgeCollect = runCollect;
  window.__wdAttributionBridgeMaybeBind = maybeBind;
})();

/**
 * GA4 form_submit bridge (openspec: add-ga4-form-submit-tracking)
 * Success → dataLayer { event: form_submit, form_id, form_name, form_location, form_type, form_provider }
 * No PII. Consent via Shopify.customerPrivacy.analyticsProcessingAllowed.
 * Catalog T2yHmU on myklpages.com needs a separate landing-page bridge (not covered here).
 */
(function () {
  var FORM_MAP = {
    UVywzG: {
      form_id: 'UVywzG',
      form_name: 'klaviyo_popup',
      form_location: 'sitewide_popup',
      form_type: 'newsletter_lead',
      form_provider: 'klaviyo'
    },
    T2yHmU: {
      form_id: 'T2yHmU',
      form_name: 'wholesale_catalog',
      form_location: 'nav_company_profile',
      form_type: 'catalog_download',
      form_provider: 'klaviyo'
    },
    clay_b2b_quote: {
      form_id: 'clay_b2b_quote',
      form_name: 'clay_b2b_quote',
      form_location: 'product_detail',
      form_type: 'b2b_quote',
      form_provider: 'clay'
    },
    contact_form: {
      form_id: 'contact_form',
      form_name: 'homepage_contact',
      form_location: 'homepage',
      form_type: 'contact',
      form_provider: 'shopify_contact'
    }
  };

  var CLAY_SUBMIT_RE = /clay-b2b-wholesale-pricing\.fly\.dev\/app\/form\/submit\/?/i;
  var recentKeys = {};
  var DEDUPE_MS = 4000;

  function analyticsAllowed() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') {
        return !!privacy.analyticsProcessingAllowed();
      }
    } catch (error) {}
    return true;
  }

  function isRecent(key) {
    var now = Date.now();
    var last = recentKeys[key] || 0;
    if (now - last < DEDUPE_MS) return true;
    recentKeys[key] = now;
    return false;
  }

  function pushFormSubmit(mapping) {
    if (!mapping || !mapping.form_id || !mapping.form_name || !mapping.form_provider) return;
    if (!analyticsAllowed()) return;
    if (isRecent('fs:' + mapping.form_id)) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'form_submit',
      form_id: mapping.form_id,
      form_name: mapping.form_name,
      form_location: mapping.form_location,
      form_type: mapping.form_type,
      form_provider: mapping.form_provider
    });
  }

  function onKlaviyoForms(event) {
    var detail = event && event.detail;
    if (!detail || detail.type !== 'submit') return;
    var formId = detail.formId != null ? String(detail.formId) : '';
    if (!formId || !FORM_MAP[formId]) return;
    pushFormSubmit(FORM_MAP[formId]);
  }

  function maybeClaySuccess(url, status) {
    if (!(status >= 200 && status < 300)) return;
    if (!CLAY_SUBMIT_RE.test(String(url || ''))) return;
    pushFormSubmit(FORM_MAP.clay_b2b_quote);
  }

  function wrapFetch() {
    if (typeof window.fetch !== 'function') return;
    var originalFetch = window.fetch;
    window.fetch = function () {
      var input = arguments[0];
      var url = typeof input === 'string' ? input : input && input.url;
      return originalFetch.apply(this, arguments).then(function (response) {
        try {
          maybeClaySuccess(url, response && response.status);
        } catch (error) {}
        return response;
      });
    };
  }

  function wrapXhr() {
    if (!window.XMLHttpRequest || !window.XMLHttpRequest.prototype) return;
    var open = XMLHttpRequest.prototype.open;
    var send = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__wdGa4FormUrl = url;
      this.__wdGa4FormMethod = method;
      return open.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      if (!xhr.__wdGa4FormSubmitBound) {
        xhr.__wdGa4FormSubmitBound = true;
        xhr.addEventListener('load', function () {
          try {
            maybeClaySuccess(xhr.__wdGa4FormUrl, xhr.status);
          } catch (error) {}
        });
      }
      return send.apply(this, arguments);
    };
  }

  function trackHomepageContactSuccess() {
    var storageKey = 'wd_ga4_fs_contact_form';
    var successEl = document.querySelector(
      '.contact-split-form [data-ga4-form-success="contact_form"], [data-ga4-form-success="contact_form"]'
    );

    if (!successEl) {
      try {
        sessionStorage.removeItem(storageKey);
      } catch (error) {}
      return;
    }

    try {
      if (sessionStorage.getItem(storageKey) === '1') return;
      sessionStorage.setItem(storageKey, '1');
    } catch (error) {}

    pushFormSubmit(FORM_MAP.contact_form);
  }

  function initFormSubmitBridge() {
    window.addEventListener('klaviyoForms', onKlaviyoForms);
    wrapFetch();
    wrapXhr();
    trackHomepageContactSuccess();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFormSubmitBridge);
  } else {
    initFormSubmitBridge();
  }

  window.__wdGa4FormSubmitPush = pushFormSubmit;
  window.__wdGa4FormSubmitMap = FORM_MAP;
})();

/**
 * Bra sizing system switcher (Phase 1: Band Size + Cup Size only).
 * Updates labels / aria-label only — never mutates option input/select values.
 */
(function () {
  var STORAGE_KEY = 'braSizeSystem';
  var ROOT_SELECTOR = 'product-variants[data-bra-size-system="true"]';

  function getMap() {
    var el = document.getElementById('bra-size-system-map');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }

  function normalizeCanonical(value) {
    return String(value || '').trim();
  }

  function mapSize(map, axis, system, canonical) {
    var key = normalizeCanonical(canonical);
    if (!key || !map || system === 'us') return key;

    var table = map[axis];
    if (!table) return key;

    var row = table[key] || table[key.toUpperCase()] || table[key.toLowerCase()];
    if (!row) return key;

    return row[system] || key;
  }

  function readStoredSystem() {
    try {
      var stored = localStorage.getItem(STORAGE_KEY);
      if (stored && ['us', 'uk', 'eu', 'fr_es', 'it'].indexOf(stored) !== -1) {
        return stored;
      }
    } catch (error) {}
    return 'us';
  }

  function writeStoredSystem(system) {
    try {
      localStorage.setItem(STORAGE_KEY, system);
    } catch (error) {}
  }

  function getActiveSystem(root) {
    var active = root.querySelector('.product-form__size-system-btn.is-active');
    return (active && active.getAttribute('data-size-system')) || readStoredSystem() || 'us';
  }

  function setActiveButton(root, system) {
    var map = getMap();
    var labelText =
      (map && map.labels && map.labels[system]) ||
      String(system || 'us').replace('_', '/').toUpperCase();

    var buttons = root.querySelectorAll('.product-form__size-system-btn');
    Array.prototype.forEach.call(buttons, function (btn) {
      var isActive = btn.getAttribute('data-size-system') === system;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    // Keep dropdown trigger label in sync (presentation only)
    var current = root.querySelector('.product-form__size-system-current');
    if (current) {
      current.textContent = labelText;
    }

    var details = root.querySelector('details.product-form__size-system');
    if (details) {
      details.open = false;
    }
  }

  function applySystem(root, system) {
    var map = getMap();
    if (!map || !root) return;

    setActiveButton(root, system);
    root.setAttribute('data-active-size-system', system);

    var selectors = root.querySelectorAll('[data-size-axis]');
    Array.prototype.forEach.call(selectors, function (selector) {
      var axis = selector.getAttribute('data-size-axis');
      if (axis !== 'band' && axis !== 'cup') return;

      var labels = selector.querySelectorAll('.product-form__size-label[data-canonical-size]');
      Array.prototype.forEach.call(labels, function (label) {
        var canonical = label.getAttribute('data-canonical-size');
        var display = mapSize(map, axis, system, canonical);
        label.textContent = display;

        var input = null;
        var forId = label.parentElement && label.parentElement.getAttribute('for');
        if (forId) {
          input = document.getElementById(forId);
        }
        if (!input) {
          var swatch = label.closest('.block-swatch');
          input = swatch && swatch.querySelector('input[type="radio"]');
        }
        if (input) {
          input.setAttribute('aria-label', display);
        }

        var optionBtn = label.closest('.combo-box__option-item');
        if (optionBtn) {
          optionBtn.setAttribute('aria-label', display);
        }
      });

      var checked = selector.querySelector('input[type="radio"]:checked');
      var selectEl = selector.querySelector('select[name^="option"]');
      var canonicalSelected = checked
        ? checked.value
        : selectEl
          ? selectEl.value
          : '';
      var displaySelected = mapSize(map, axis, system, canonicalSelected);

      var valueEls = selector.querySelectorAll('.product-form__option-value, .select__selected-value');
      Array.prototype.forEach.call(valueEls, function (valueEl) {
        if (canonicalSelected) {
          valueEl.setAttribute('data-canonical-size', canonicalSelected);
          valueEl.textContent = displaySelected;
        }
      });
    });
  }

  function initRoot(root) {
    if (!root || root.dataset.braSizeSystemReady === 'true') return;
    if (!getMap()) return;

    root.dataset.braSizeSystemReady = 'true';
    var system = readStoredSystem();
    applySystem(root, system);

    var ui = root.querySelector('[data-bra-size-system-ui]');
    if (ui) {
      ui.addEventListener('click', function (event) {
        var btn = event.target.closest('.product-form__size-system-btn');
        if (!btn || !root.contains(btn)) return;
        var next = btn.getAttribute('data-size-system');
        if (!next) return;
        writeStoredSystem(next);
        applySystem(root, next);
      });
    }

    root.addEventListener('change', function (event) {
      if (!event.target || !event.target.name || event.target.name.indexOf('option') !== 0) return;
      var system = getActiveSystem(root);
      // InputBindingManager writes canonical value into bound spans on change; re-apply after it.
      setTimeout(function () {
        applySystem(root, system);
      }, 0);
    });
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll(ROOT_SELECTOR), initRoot);
  }

  document.addEventListener('variant:changed', function (event) {
    var form = event.target;
    if (!form || !form.id) return;
    var root = document.querySelector(
      ROOT_SELECTOR + '[form-id="' + CSS.escape(form.id) + '"]'
    );
    if (!root) return;
    var system = getActiveSystem(root);
    setTimeout(function () {
      applySystem(root, system);
    }, 0);
  });

  document.addEventListener('shopify:section:load', function () {
    initAll();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
